import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Package, Truck, MapPin, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const stageConfig = {
  material_received: {
    label: "Material Received",
    icon: Package,
    description: "Package received at facility"
  },
  material_despatched: {
    label: "Material Despatched",
    icon: Truck,
    description: "Package in transit"
  },
  out_for_delivery: {
    label: "Out for Delivery",
    icon: MapPin,
    description: "Package is out for delivery"
  },
  delivered: {
    label: "Delivered",
    icon: CheckCircle2,
    description: "Successfully delivered"
  }
};

const stageOrder = ["material_received", "material_despatched", "out_for_delivery", "delivered"];

const TrackingSection = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setError("Please enter a tracking number");
      return;
    }

    setLoading(true);
    setError("");
    setTrackingResult(null);

    try {
      const response = await axios.get(`${BACKEND_URL}/api/track/${trackingNumber.trim().toUpperCase()}`);
      setTrackingResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Consignment not found. Please check your tracking number.");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getCurrentStageIndex = () => {
    if (!trackingResult) return -1;
    return stageOrder.indexOf(trackingResult.current_stage);
  };

  return (
    <section id="tracking" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#E63946]">
            Shipment Status
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B192C] tracking-tight">
            Track Your Consignment
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mt-4">
            Enter your tracking number to get real-time updates on your shipment
          </p>
        </motion.div>

        {/* Search Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <form onSubmit={handleTrack} className="relative" data-testid="tracking-form">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter Tracking Number (e.g., SKT20240101ABC123)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-slate-200 focus:border-[#E63946] focus:outline-none text-[#0B192C] font-medium transition-colors"
                  data-testid="tracking-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-4 bg-[#E63946] text-white font-semibold rounded-xl hover:bg-[#c92a37] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="tracking-submit"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Tracking...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Track
                  </>
                )}
              </button>
            </div>
          </form>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700"
              data-testid="tracking-error"
            >
              <AlertCircle className="w-5 h-5" />
              {error}
            </motion.div>
          )}
        </motion.div>

        {/* Tracking Result */}
        {trackingResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl mx-auto"
            data-testid="tracking-result"
          >
            {/* Consignment Info Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 mb-8">
              <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Tracking Number</p>
                  <p className="text-2xl font-bold text-[#0B192C]" data-testid="result-tracking-number">
                    {trackingResult.tracking_number}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full font-semibold text-sm ${
                  trackingResult.current_stage === 'delivered' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {stageConfig[trackingResult.current_stage]?.label || trackingResult.current_stage}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">From</p>
                    <p className="font-semibold text-[#0B192C]">{trackingResult.sender_name}</p>
                    <p className="text-slate-600 text-sm">{trackingResult.origin}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">To</p>
                    <p className="font-semibold text-[#0B192C]">{trackingResult.receiver_name}</p>
                    <p className="text-slate-600 text-sm">{trackingResult.destination}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tracking Timeline - Horizontal */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
              <h3 className="text-lg font-bold text-[#0B192C] mb-8">Shipment Progress</h3>
              
              <div className="relative">
                {/* Progress Line */}
                <div className="hidden md:block absolute top-[5.25rem] left-0 right-0 h-1 bg-slate-200 mx-16" />
                <div 
                  className="hidden md:block absolute top-[5.25rem] left-0 h-1 bg-[#E63946] mx-16 transition-all duration-500"
                  style={{ width: `calc(${(getCurrentStageIndex() / (stageOrder.length - 1)) * 100}% - 8rem)` }}
                />

                {/* Stages */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {stageOrder.map((stageKey, index) => {
                    const stage = trackingResult.stages?.find(s => s.stage === stageKey);
                    const config = stageConfig[stageKey];
                    const Icon = config.icon;
                    const isCompleted = stage?.status;
                    const isCurrent = trackingResult.current_stage === stageKey;
                    const dateTime = formatDate(stage?.timestamp);

                    return (
                      <div key={stageKey} className="flex flex-col items-center text-center">
                        {/* Date/Time above */}
                        <div className="min-h-[56px] flex flex-col justify-end items-center mb-3 px-1">
                          {dateTime ? (
                            <>
                              <p className="text-sm font-semibold text-[#E63946] whitespace-nowrap leading-tight">{dateTime.time}</p>
                              <p className="text-xs text-slate-600 whitespace-nowrap leading-tight mt-0.5">{dateTime.date}</p>
                            </>
                          ) : (
                            <p className="text-xs text-slate-300">Pending</p>
                          )}
                        </div>

                        {/* Icon Circle */}
                        <div 
                          className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                            isCompleted 
                              ? 'bg-[#E63946] text-white shadow-lg shadow-red-500/30' 
                              : 'bg-slate-100 text-slate-400'
                          } ${isCurrent ? 'ring-4 ring-[#E63946]/30 scale-110' : ''}`}
                        >
                          <Icon className="w-7 h-7" />
                        </div>

                        {/* Stage Label */}
                        <div className="mt-4">
                          <p className={`font-semibold ${isCompleted ? 'text-[#0B192C]' : 'text-slate-400'}`}>
                            {config.label}
                          </p>
                          <p className="text-xs text-slate-500 mt-1 max-w-[150px]">
                            {stage?.description || config.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TrackingSection;
